import JSZip from 'jszip';
import { safeParseProject, type PhotobookProject, type PageElement } from '@fotoboek/domain';
import { assetBlobStore } from './blobStore';
import { saveProject } from './projectRepository';

const MANIFEST_VERSION = 1;

type BackupManifest = {
  formatVersion: number;
  exportedAt: string;
  project: { id: string; title: string; schemaVersion: number; revision: number };
  assets: Array<{
    id: string;
    fileName: string;
    checksumSha256: string;
    mimeType: string;
    byteSize: number;
  }>;
};

function extensionForMime(mime: string): string {
  switch (mime) {
    case 'image/png':
      return 'png';
    case 'image/webp':
      return 'webp';
    case 'image/heic':
      return 'heic';
    case 'image/heif':
      return 'heif';
    default:
      return 'jpg';
  }
}

async function sha256Hex(data: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Reject any zip entry path that could escape the intended extraction root
 * ("zip-slip"). We never trust the archive's paths for filesystem writes —
 * this check is a defense-in-depth guard even though we also never use raw
 * zip paths directly as storage keys below.
 */
function isSafeZipPath(path: string): boolean {
  if (path.startsWith('/') || path.startsWith('\\')) return false;
  if (/^[a-zA-Z]:[\\/]/.test(path)) return false;
  // Directory entries end in a trailing slash (e.g. "assets/") — strip it
  // before splitting so that benign folder markers aren't mistaken for a
  // path with an empty segment.
  const trimmed = path.replace(/[\\/]+$/, '');
  if (trimmed.length === 0) return false;
  const segments = trimmed.split(/[\\/]/);
  return !segments.includes('..') && segments.every((s) => s.length > 0);
}

export async function exportProjectBackup(project: PhotobookProject): Promise<Blob> {
  const zip = new JSZip();
  const assetsFolder = zip.folder('assets');
  if (!assetsFolder) throw new Error('Kan geen assets-map aanmaken in het backupbestand.');
  const manifestAssets: BackupManifest['assets'] = [];

  for (const asset of project.assets) {
    const blob = await assetBlobStore.get(project.id, asset.id, 'original');
    if (!blob) continue;
    const buffer = await blob.arrayBuffer();
    const checksum = await sha256Hex(buffer);
    const ext = extensionForMime(asset.mimeType);
    const fileName = `${asset.id}.${ext}`;
    assetsFolder.file(fileName, buffer);
    manifestAssets.push({
      id: asset.id,
      fileName,
      checksumSha256: checksum,
      mimeType: asset.mimeType,
      byteSize: buffer.byteLength,
    });
  }

  const manifest: BackupManifest = {
    formatVersion: MANIFEST_VERSION,
    exportedAt: new Date().toISOString(),
    project: {
      id: project.id,
      title: project.title,
      schemaVersion: project.schemaVersion,
      revision: project.revision,
    },
    assets: manifestAssets,
  };

  zip.file('manifest.json', JSON.stringify(manifest, null, 2));
  zip.file('project.json', JSON.stringify(project, null, 2));

  return zip.generateAsync({ type: 'blob', compression: 'DEFLATE' });
}

/** Bundles every project's individual `.fotoboek` backup into one zip of zips. */
export async function exportAllProjectsBackup(projects: PhotobookProject[]): Promise<Blob> {
  const outer = new JSZip();
  for (const project of projects) {
    const inner = await exportProjectBackup(project);
    const safeName = project.title.replace(/[^a-z0-9-_ ]/gi, '_').trim() || 'fotoboek';
    outer.file(`${safeName}-${project.id}.fotoboek`, inner);
  }
  return outer.generateAsync({ type: 'blob', compression: 'DEFLATE' });
}

export type ImportBackupResult =
  { status: 'ok'; project: PhotobookProject } | { status: 'invalid'; message: string };

function remapElement(el: PageElement, idMap: Map<string, string>): PageElement {
  if (el.type === 'photoFrame' && el.assetId) {
    return { ...el, assetId: idMap.get(el.assetId) ?? null };
  }
  return el;
}

function remapProjectIds(
  project: PhotobookProject,
  newProjectId: string,
  idMap: Map<string, string>,
): PhotobookProject {
  return {
    ...project,
    id: newProjectId,
    cloudProjectId: undefined,
    ownerMode: 'local',
    assets: project.assets.map((a) => ({ ...a, id: idMap.get(a.id) ?? a.id })),
    pages: project.pages.map((p) => ({
      ...p,
      elements: p.elements.map((el) => remapElement(el, idMap)),
    })),
    cover: {
      ...project.cover,
      panels: {
        front: {
          ...project.cover.panels.front,
          elements: project.cover.panels.front.elements.map((el) => remapElement(el, idMap)),
        },
        back: {
          ...project.cover.panels.back,
          elements: project.cover.panels.back.elements.map((el) => remapElement(el, idMap)),
        },
        spine: {
          ...project.cover.panels.spine,
          elements: project.cover.panels.spine.elements.map((el) => remapElement(el, idMap)),
        },
      },
    },
  };
}

/**
 * Restores a `.fotoboek` backup as a brand-new local project. Importing
 * always mints fresh project/asset ids so a restore can never silently
 * clobber an existing project that happens to share the original id.
 */
export async function importProjectBackup(file: Blob): Promise<ImportBackupResult> {
  let zip: JSZip;
  try {
    zip = await JSZip.loadAsync(file);
  } catch {
    return { status: 'invalid', message: 'Dit is geen geldig .fotoboek-backupbestand.' };
  }

  for (const path of Object.keys(zip.files)) {
    if (!isSafeZipPath(path)) {
      return { status: 'invalid', message: 'Het backupbestand bevat onveilige bestandspaden.' };
    }
  }

  const manifestEntry = zip.file('manifest.json');
  const projectEntry = zip.file('project.json');
  if (!manifestEntry || !projectEntry) {
    return { status: 'invalid', message: 'Het backupbestand mist manifest.json of project.json.' };
  }

  let manifest: BackupManifest;
  try {
    manifest = JSON.parse(await manifestEntry.async('string'));
  } catch {
    return { status: 'invalid', message: 'manifest.json kan niet worden gelezen.' };
  }

  if (manifest.formatVersion > MANIFEST_VERSION) {
    return {
      status: 'invalid',
      message: 'Dit backupbestand is gemaakt met een nieuwere versie van de app.',
    };
  }

  let rawProject: unknown;
  try {
    rawProject = JSON.parse(await projectEntry.async('string'));
  } catch {
    return { status: 'invalid', message: 'project.json kan niet worden gelezen.' };
  }

  const parsedResult = safeParseProject(rawProject);
  if (!parsedResult.success) {
    return { status: 'invalid', message: parsedResult.error };
  }

  const idMap = new Map<string, string>();
  for (const asset of parsedResult.data.assets) {
    idMap.set(asset.id, crypto.randomUUID());
  }
  const newProjectId = crypto.randomUUID();
  const remappedProject = remapProjectIds(parsedResult.data, newProjectId, idMap);

  for (const manifestAsset of manifest.assets) {
    const entry = zip.file(`assets/${manifestAsset.fileName}`);
    if (!entry) continue;
    const buffer = await entry.async('arraybuffer');
    const checksum = await sha256Hex(buffer);
    if (checksum !== manifestAsset.checksumSha256) {
      return {
        status: 'invalid',
        message: `Controlesom van ${manifestAsset.fileName} komt niet overeen — bestand is mogelijk beschadigd.`,
      };
    }
    const newAssetId = idMap.get(manifestAsset.id);
    if (!newAssetId) continue;
    const blob = new Blob([buffer], { type: manifestAsset.mimeType });
    await assetBlobStore.put(newProjectId, newAssetId, 'original', blob);
  }

  await saveProject(remappedProject);
  return { status: 'ok', project: remappedProject };
}
